import Button from 'components/base/Button';
import DatePicker from 'components/base/DatePicker';
import { Col, FloatingLabel, Form, Row } from 'react-bootstrap';
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Movimiento, mtoInicial } from 'data/project-management/Movimiento';
import { TipoDeMovimiento, tipoDeMtoInicial } from 'data/project-management/tipoDeMovimiento';
import { Cobro, cobroInicial } from 'data/project-management/Cobro';
import axios, { AxiosError } from 'axios';
import { TextEditor } from 'components/Cap/TextEditor';
import { useAuth } from 'providers/AuthProvider';
import { formatMoney } from 'helpers/utils';
import CobroDetail from './CobroDetail';
import { Texto } from 'data/project-management/texto';

interface MovimientoComponentProps {
  movimientoId?: string | number;
  isModal?: boolean;
  onClose?: () => void;
}

const MovimientoComponent: React.FC<MovimientoComponentProps> = ({ movimientoId, isModal, onClose }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Compatibilidad con uso fuera de modal
  const { id: urlId } = useParams();
  const id = movimientoId || urlId;

  const [mto, setMto] = useState<Movimiento>(mtoInicial);
  const [texto, setTexto] = useState('Sr Juez: PARKES CRISTIAN ANDRES');
  const [fecha, setFecha] = useState<Date>(new Date());
  const [realizado] = useState<number>(1);
  const [tmto, setTmto] = useState<TipoDeMovimiento>(tipoDeMtoInicial);
  const [tipos, setTipos] = useState<TipoDeMovimiento[]>([]);
  const [cobro, setCobro] = useState<Cobro>(cobroInicial);

  const autos = `${mto.Proc.ACTO} C/ ${mto.Proc.DEMA} S/ ${mto.Proc.TPRO}`;
  const autos_cortos = `${mto.Proc.ACTO} C/ ${mto.Proc.DEMA.split(' ')[0]} S/ ${mto.Proc.TPRO.split(' ')[0]}`;
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:2000'; 
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch Movimiento
  useEffect(() => {
    if (!id) return;
    const fetchMto = async () => {
      try {
        const response = await axios.get(`${apiUrl}/mtos/id/${id}`);
        setMto(response.data);
      } catch (error) {
        console.error('Error fetching movimiento:', error);
      }
    };
    fetchMto();
  }, [id, apiUrl]);

  // (El resto de tus useEffects se mantiene intacto)
  useEffect(() => {
    const fetchTMto = async () => {
      try {
        const response = await axios.get(`${apiUrl}/tMtos`);
        setTipos(response.data);
      } catch (error) {
        console.error('Error fetching tipos:', error);
      }
    };
    fetchTMto();
  }, [apiUrl]);

  useEffect(() => {
    if (mto.fecha) {
      const parsedDate = new Date(mto.fecha);
      if (!isNaN(parsedDate.getTime())) {
        setFecha(parsedDate);
      } else {
        setFecha(new Date());
      }
    }
  }, [mto]);

  useEffect(() => {
    const fetchCobro = async () => {
      if (mto.clase_de_mto === 'Cobro' && mto.clase_fk) {
        try {
          const response = await axios.get(`${apiUrl}/cobros/id/${mto.clase_fk}`);
          setCobro(response.data);
        } catch (error) {
          console.error('Error fetching cobro:', (error as AxiosError).response?.data || (error as AxiosError).message);
        }
      }
    };
    fetchCobro();
  }, [mto.clase_fk, apiUrl]);

  // (Sync campos descriptivos y handlers)
  useEffect(() => {
    const isSaleTranfeActive = ['Cobro'].includes(tmto.clase);
    if (isSaleTranfeActive && (cobro.monto || cobro.quien_cobra)) {
      const newDescripcion = `Cobro: ${formatMoney(cobro.monto)} - (${cobro.capital_honorarios}) ${
        cobro.capital_honorarios === 'Capital' ? ' - PCL: ' + formatMoney(cobro.PCL) : ''
      }- Cobra: ${cobro.quien_cobra} - ${cobro.estado}`;
      const newNombreCobro = `${autos_cortos || ''} - ${cobro.capital_honorarios} - $ ${cobro.monto} - PCL: $${cobro.PCL}- Cobra: ${cobro.quien_cobra}`;
      setMto(prev => ({ ...prev, descripcion: newDescripcion }));
      setCobro(prev => ({ ...prev, nombre: newNombreCobro }));
    }
  }, [cobro.monto, cobro.quien_cobra, cobro.capital_honorarios, cobro.PCL, cobro.estado, tmto.clase, autos_cortos]);

  // IMPORTANTE: Pon este useEffect junto a los otros useEffects (por ejemplo, después de cargar mto)
  
  // FETCH TEXTO SEPARADO
  useEffect(() => {
    const fetchTexto = async () => {
      // Si el movimiento NO es Cobro y tiene un ID en clase_fk, buscamos el texto
      if (mto.clase_de_mto !== 'Cobro' && mto.clase_fk) {
        try {
          const response = await axios.get(`${apiUrl}/textos/id/${mto.clase_fk}`);
          if (response.data && response.data.texto) {
            setTexto(response.data.texto);
          }
        } catch (error) {
          console.error('Error fetching texto asociado:', error);
        }
      }
    };

    // Nos aseguramos que mto ya haya sido cargado desde la API antes de disparar esto
    if (mto.id) {
      fetchTexto();
    }
  }, [mto.id, mto.clase_de_mto, mto.clase_fk, apiUrl]);


// REEMPLAZA TU FUNCIÓN handleSubmitMtoOnly CON ESTA:

  const handleSubmitMtoOnly = async (e: React.MouseEvent<HTMLButtonElement>, updatedMto?: Movimiento) => {
    if (e) e.preventDefault();
    setIsSubmitting(true);
    try {
      let mtoToSave = updatedMto || mto;

      // 1. SI SE USO EL EDITOR DE TEXTO, GUARDAMOS EN LA TABLA `TEXTOS` PRIMERO
      if (tmto.clase !== 'Cobro') {
        if (mtoToSave.clase_fk) {
          // Si ya tenía un texto asignado, lo actualizamos
          await axios.post(`${apiUrl}/textos/update`, {
            id: mtoToSave.clase_fk,
            texto: texto
          });
        } else {
          // Si no tenía texto asignado (movimiento nuevo), lo creamos
          const responseTexto = await axios.post(`${apiUrl}/textos/store`, {
            texto: texto
          });
          
          // Capturamos el nuevo ID que nos devuelve el backend y lo asignamos al movimiento
          const nuevoTextoId = responseTexto.data.id;
          mtoToSave = { ...mtoToSave, clase_fk: nuevoTextoId, clase_de_mto: tmto.clase || 'Texto' };
        }
      }

      // 2. GUARDAMOS EL MOVIMIENTO (fíjate que quité el parámetro `texto` del payload)
      await axios.post(`${apiUrl}/mtos/update`, {
        id: mtoToSave.id,
        descripcion: mtoToSave.descripcion,
        realizado,
        fecha: fecha,
        clase_fk: mtoToSave.clase_fk,         // Acá ya viaja el ID del texto o del cobro
        clase_de_mto: mtoToSave.clase_de_mto, 
        tipo_de_mto: tmto.id || mtoToSave.tipo_de_mto,
        usuario: user?.iniciales
      });
      
      // Control de cierre si estamos en Modal o Navegación si es página completa
      if (isModal && onClose) {
        onClose();
      } else {
        navigate(`/app/apps/project-management/proceso-details/${mto.proc}`);
      }

    } catch (error) {
      console.error('Error updating movimiento:', error);
      alert('Error al guardar');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDescripcionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMto(prev => ({ ...prev, descripcion: event.target.value }));
  };

  const handleTextChange = (newValue: string) => setTexto(newValue);
  const handleTMtoSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    // ... tu lógica
    const selectedValue = event.target.value;
    const selectedTipo = tipos.find(tipo => tipo.nombre === selectedValue) || tipoDeMtoInicial;

    setTmto(selectedTipo);
    setMto(prev => ({
      ...prev,
      tipo_de_mto: selectedTipo.id,
      clase_de_mto: selectedTipo.clase,
      descripcion: selectedTipo.texto || prev.descripcion
    }));

    let cobra = ''; let ch = ''; let estado = '';

    switch (selectedTipo.id) {
      case 'sale_transferencia_para_actor': cobra = 'CASH'; ch = 'Capital'; estado = 'Pendiente'; break;
      case 'sale_transferencia_para_geo': cobra = 'GEO'; ch = 'Honorarios'; estado = 'Cobrado'; break;
      case 'sale_transferencia_para_cap': cobra = 'CAP'; ch = 'Honorarios'; estado = 'Cobrado'; break;
      case 'sale_transferencia_para_mvp': cobra = 'MVP'; ch = 'Honorarios'; estado = 'Cobrado'; break;
      case 'sale_transferencia_para_sag': cobra = 'SAG'; ch = 'Honorarios'; estado = 'Cobrado'; break;
      case 'sale_transferencia_para_is': cobra = 'IS'; ch = 'Honorarios'; estado = 'Cobrado'; break;
      case 'sale_transferencia_para_isv': cobra = 'ISV'; ch = 'Honorarios'; estado = 'Cobrado'; break;
      case 'sale_transferencia_para_zcc': cobra = 'ZCC'; ch = 'Honorarios'; estado = 'Cobrado'; break;
      case 'sale_transferencia_para_la': cobra = 'LA'; ch = 'Honorarios'; estado = 'Cobrado'; break;
      case 'sale_transferencia_para_sucesion_jap': cobra = 'SUCESION'; ch = 'Honorarios'; estado = 'Cobrado'; break;
      case 'sale_transferencia_para_ea': cobra = 'EA'; ch = 'Honorarios'; estado = 'Cobrado'; break;
      case 'ddo_paga_honorarios_cash': cobra = 'CASH'; ch = 'Honorarios'; estado = 'Cobrado'; break;
    }

    setCobro(prev => ({
      ...prev,
      mtos_fk: mto.id,
      quien_cobra: cobra,
      capital_honorarios: ch,
      estado: estado,
      usuario: user?.iniciales,
      proc: mto.proc
    }));
  };  

  const handleSubmitCobro = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const newDescripcion = `Cobro: $${cobro.monto}(${cobro.capital_honorarios}) ${
      cobro.capital_honorarios === 'Capital' ? ' - PCL: $' + cobro.PCL : ''
    }- Cobra: ${cobro.quien_cobra} - ${cobro.estado}`;

    const newNombreCobro = `${autos_cortos || ''} - ${cobro.capital_honorarios} - $ ${cobro.monto} - PCL: $${cobro.PCL}- Cobra: ${cobro.quien_cobra}`;

    const cobroFinal = { ...cobro, proc: mto.proc, usuario: user?.iniciales, nombre: newNombreCobro };

    const confirmSave = window.confirm(
      `Estás guardando este cobro:\n
      Concepto: ${cobroFinal.capital_honorarios}\n
      Monto: $ ${formatMoney(cobroFinal.monto || 0)}\n
      PCL: $ ${formatMoney(cobroFinal.PCL || 0)}\n
      Estado: ${cobroFinal.estado}\n
      Cobra: ${cobroFinal.quien_cobra}\n
      Modo de pago: ${cobroFinal.libranza_judicial_transferencia_directa}\n
      Notas: ${cobroFinal.notas}\n\n
      ¿Confirmas?`
    );

    if (!confirmSave) {
      setIsSubmitting(false);
      return;
    }

    try {
      const { cobro_id, ...cobroData } = cobroFinal;
      const response = await axios.post(`${apiUrl}/cobros/store`, cobroData);
      const newCobroId = response.data;

      const updatedMto = {
        ...mto,
        descripcion: newDescripcion,
        clase_fk: newCobroId,
        tipo_de_mto: tmto.id,
        clase_de_mto: tmto.clase
      };

      setMto(updatedMto);
      await handleSubmitMtoOnly(e, updatedMto);
    } catch (error) {
      console.error('Error updating cobro:', (error as AxiosError).response?.data || (error as AxiosError).message);
      alert('Error al guardar');
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (isModal && onClose) {
      onClose();
    } else {
      navigate(`/app/apps/project-management/proceso-details/${mto.proc}`);
    }
  };

  return (
    <div className="p-4">
      <h2 className="mb-4">Movimiento en {autos}</h2>

      <Row className="g-3 mb-4">
        <Col xs={12} md={4}>
          <DatePicker value={fecha} />
        </Col>
      </Row>

      <Row className="g-3 mb-4">
        <Col xs={12} md={4}>
          <Form.Select
            size="lg"
            onChange={handleTMtoSelectChange}
            value={tipos.find(tipo => tipo.id === mto.tipo_de_mto)?.nombre || ''}
          >
            <option value="">Seleccione Tipo de Movimiento</option>
            {tipos.map(tipo => (
              <option key={tipo.id} value={tipo.nombre}>{tipo.nombre}</option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      {/* RENDERIZADO UNIFICADO DE COBRODETAIL */}
      {tmto.clase === 'Cobro' && (
        <CobroDetail
          isEmbedded={true}
          cobroData={cobro}
          onChangeCobro={setCobro}
          onSubmitEmbedded={handleSubmitCobro}
          isSubmittingEmbedded={isSubmitting}
        />
      )}

      {tmto.clase !== 'Cobro' && (
        <div>
          <Row className="g-3 mb-4">
            <Col xs={12}>
              <FloatingLabel controlId="descripcion" label="Descripción">
                <Form.Control
                  size="lg"
                  type="text"
                  value={mto.descripcion}
                  onChange={handleDescripcionChange}
                />
              </FloatingLabel>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col xs={12}>
              <div style={{ height: '300px' }}>
                <TextEditor
                  text={texto}
                  titulo={tmto.titulo}
                  cuerpo={tmto.texto}
                  autos={autos}
                  handleTextChange={handleTextChange}
                />
              </div>
            </Col>
          </Row>

          <Row className="justify-content-end">
            <Col xs="auto" className="d-flex gap-3 mt-4">
              <Button variant="secondary" className="px-5" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                variant="primary"
                className="px-5"
                onClick={handleSubmitMtoOnly}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Guardando...' : 'Guardar'}
              </Button>
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
};

export default MovimientoComponent;